import getRandomLifestyleImage from 'utils/getLifestyleImage';
import createPictureImageSources from 'utils/createPictureImageSources';
import LotStatus from '../../enums/lotStatus';

/**
 * Represents a Paid Interactive Site Plan Summary Card component.
 */
export default class PaidInteractiveSitePlanHousingUnitCard {
  /**
   * Sets the information for the summary card.
   *
   * @param {Object} configuration - The configuration object for the summary card.
   */
    setInformation(configuration) {
    const {summaryCard, lotNumber, availableSpecCount, availablePlanCount, lotStatus, lotId} = configuration;
    this.summaryCard = summaryCard;
    this.id = 'summaryCard' + this.summaryCard.Id;
    this.lotNumber = lotNumber;
    this.availablePlanCount = availablePlanCount;
    this.availableSpecCount = availableSpecCount;
    this.lotStatus = lotStatus;
    this.lotId = lotId;
    this.errorImage = getRandomLifestyleImage();
    const { sources, src } = createPictureImageSources({
      url: this.summaryCard.Thumbnail ?? this.errorImage,
      breakpoints: {
        '2x': {
          width: 286,
          height: 208,
        },
        '3x': {
          width: 350,
          height: 312,
        },
      },
      irsParameters: {
        mode: 'crop',
        progressive: true,
      },
    });
    this.sources = sources;
    this.src = src;
  }

  /**
   * Sets the label for the PaidSummaryCard.
   * @param {string} label - The label to set.
   */
  setLabel(label) {
    this.label = label;
  }

  /**
   * Handles the error event for the image element in the summary card.
   * If the image fails to load, it sets the error image as the source and removes all source elements from the parent
   * @param {Event} event - The error event object.
   */
  handleOnErrorImage(event) {
    const picture = event.target.parentElement;
    event.target.src = this.errorImage;

    picture?.querySelectorAll('source').forEach((source) => source.remove());
    event.target.onerror = null;
  }

  /**
   * Binds events to the summary card.
   */
  bindEvents() {
    const element = this.getElement();

    const image = element.querySelector('img');

    // remove all sources and set the lifestyle error image as the src for the image
    // if the image fails loading.
    image?.addEventListener('error', this.handleOnErrorImage.bind(this));

    const anchor = element.querySelector('[data-interactive-site-plan-summary-card-link]');
    anchor?.addEventListener('click', () => {
      const summaryCard = {
        communityId: this.summaryCard.CommunityId,
        communityName: this.summaryCard.CommunityName,
        builderId: this.summaryCard.BuilderId,
        brandName: this.summaryCard.BrandName,
        specId: this.summaryCard.Id,
        lotId: this.lotId 
      };

      document.body.dispatchEvent(
        new CustomEvent('interactiveSitePlanSummaryCardClick', {
          detail: { summaryCard },
        })
      );
    });
  }

  /**
   * Gets the HTML element with the specified ID.
   * @returns {HTMLElement} The HTML anchor element.
   */
  getElement() {
    return document.getElementById(this.id);
  }

  /**
   * Get the title of the summary card.
   * @returns {string} The title of the summary card.
   */
  getTitle() {
    return `<h3 class="nhs-c-card--interactive-siteplan__price"><span class='nhs-u-text--ellipsis'>Lot ${this.lotNumber}</span></h3>`;
  }

  /**
   * Gets the sub title of the summary card.
   * @returns {string} The sub title of the summary card.
   */
  getSubTitle() {
    const statusToShow = [LotStatus.Unreleased];
    const status = LotStatus.fromKey(this.lotStatus);
    const subTitleElements = [];

    if (statusToShow.includes(status)) {
      subTitleElements.push(`<h4 class="nhs-c-card--interactive-siteplan__name nhs-u-text--ellipsis">${status.toString()}</h4>`);
    }

    const specLabel = `${this.availableSpecCount} Spec Available`;
    subTitleElements.push(`<h4 class="nhs-c-card--interactive-siteplan__name nhs-u-text--ellipsis">${specLabel}</h4>`);

    return subTitleElements.join('');
  }

  /**
   * Retrieves the image element for the summary card.
   *
   * @returns {string} The HTML string representing the image element.
   */
  getImage() {
    return `
      <picture class='nhs-c-card--interactive-siteplan__image' data-marketId="${this.summaryCard.Id}">
        ${this.sources.map((source) => `<source srcset="${source.srcset}" type="${source.format}"></source>`).join('')}
        <img
          src="${this.src}"
          data-qa="map-image"
          data-summary-img
          class='nhs-c-card--interactive-siteplan__image'
          data-src="${this.errorImage}"
        >
      </picture>
    `;
  }

  /**
   * Returns the HTML representation of the summary card.
   * @returns {string} The HTML string representing the summary card.
   */
  getHtml() {
    return `
      <div id="summaryCard${this.summaryCard.Id}"
           data-summary-content
           class="nhs-c-card--interactive-siteplan nhs-mq-card--interactive-siteplan-medium nhs-c-carousel__element"
           data-card
           data-id="${this.summaryCard.Id}">
        ${this.label.getHtml()}
        ${this.getImage()}
        <a href="${this.summaryCard.Url}" 
           data-interactive-site-plan-summary-card-link
           target="_blank" 
           class="nhs-c-card--interactive-siteplan__body nhs-u-flex-column nhs-u-justify-content-space-between">
          <div class='nhs-u-flex-column nhs-u-gap--100'>
            ${this.getTitle()}
            ${this.getSubTitle()}
          </div>
          <button 
            class="nhs-c-button--primary-lead nhs-c-button--small-24 nhs-c-button--text-only" 
            data-summary-card-cta>
            View more details
          </button>    
        </a>
      </div>
    `;
  }
}
